import axios from 'axios';
import { baseUrl } from '../hooks/useEndpoints';

export interface IExerciseHistoryTraining {
  workoutLogId: string;
  date: string;
  workoutName?: string;
  sets: Array<{
    reps?: number;
    loadActual?: number;
    loadActualUnit?: string;
    meanVelocity?: number;
    rpe?: number;
    laterality?: string;
    completed?: boolean;
  }>;
  rpe?: number;
  duration?: number;
}

export interface IExerciseHistoryTestResult {
  _id: string;
  protocolId?: string;
  protocolName?: string;
  date: string;
  value: number;
  unit: string;
  values?: Record<string, number>;
  isPR?: boolean;
  sourceType?: string;
  notes?: string;
}

export interface IExerciseHistoryProfile {
  _id: string;
  protocolId: string;
  protocolName?: string;
  profilingHubProtocolId?: string;
  sessionDate: string;
  outputs: Record<string, any>;
}

export interface IExerciseHistoryResponse {
  exerciseId: string;
  exercise: {
    name: { en: string; it: string; es: string };
    category?: string;
    recommendedForTesting?: boolean;
  };
  trainings: IExerciseHistoryTraining[];
  testResults: IExerciseHistoryTestResult[];
  profiles: IExerciseHistoryProfile[];
  meta: {
    trainingsCount: number;
    testResultsCount: number;
    profilesCount: number;
    currentPR?: { value: number; unit: string; date: string };
  };
}

export interface ITestingProtocol {
  _id: string;
  name: string;
  exerciseId?: string;
  type: 'single' | 'profile';
  profilingHubProtocolId?: string;
  metrics: Array<{ key: string; required: boolean; unit: string; label?: string }>;
  executionProtocol?: string;
  normativeData?: Record<string, any>;
  isCustom: boolean;
  teamId?: string;
}

export interface IProfilingResult {
  _id: string;
  athleteId: string;
  protocolId: string;
  exerciseId?: string;
  sessionDate: string;
  inputs: any[];
  outputs: Record<string, any>;
  notes?: string;
}

export interface IComputeProfilingPayload {
  athleteId: string;
  teamId?: string;
  sessionDate?: string;
  inputs: any[];
  notes?: string;
  bodyMass?: number;
  hPushOff?: number;
  /** 1RM reale noto (kg) per il ramo VBT "1RM noto" → MVT individuale (sez.6.1.1) */
  known1RM?: number;
}

/** Esito risoluzione %→assoluto di una singola intensità a step (BUG E). */
export interface IStepResolved {
  absValue: number | null;
  absUnit?: string;
  pace?: number;
  paceFormatted?: string;
  refValue?: number;
  refDate?: string;
  unresolved?: boolean;
  /** es. no_profile_vam | no_profile_vift | no_ftp_protocol | incomplete | vam_implausible */
  reason?: string;
}

export interface IResolvedStepNode {
  kind: 'single' | 'group';
  intensity?: { ref?: string; pct?: number; absValue?: number; absUnit?: string; resolved?: IStepResolved };
  recovery?: { intensity?: { resolved?: IStepResolved } };
  setRecovery?: { intensity?: { resolved?: IStepResolved } };
  children?: IResolvedStepNode[];
}

export const profilingEndpoints = {
  getExerciseHistory: (athleteId: string, exerciseId: string, opts?: { limitTrainings?: number; limitTests?: number }) =>
    axios.get<IExerciseHistoryResponse>(
      `${baseUrl}/profiling/athletes/${athleteId}/exercises/${exerciseId}/history`,
      { params: opts },
    ),

  computeProfile: (protocolId: string, payload: IComputeProfilingPayload) =>
    axios.post<IProfilingResult>(`${baseUrl}/profiling/${protocolId}/compute`, payload),

  /**
   * Risolve le intensità a step (% di VAM/VIFT/FTP) in valori assoluti per il
   * singolo atleta (BUG E preview). Fallback non bloccante: gli step senza
   * profilo tornano `resolved.unresolved=true`.
   */
  resolveConditioning: (payload: { athleteId: string; steps: any[] }) =>
    axios.post<{ steps: IResolvedStepNode[] }>(
      `${baseUrl}/profiling/conditioning/resolve`,
      payload,
    ),

  listAthleteProfiles: (athleteId: string, opts?: { protocolId?: string; exerciseId?: string; limit?: number }) =>
    axios.get<IProfilingResult[]>(
      `${baseUrl}/profiling/athletes/${athleteId}/profiles`,
      { params: opts },
    ),

  // Lista protocolli testing — endpoint dedicato (non admin-raw, funziona in produzione)
  // Response: array diretto (non paginato)
  listProtocols: (filter?: { exerciseId?: string }) =>
    axios.get<ITestingProtocol[]>(`${baseUrl}/profiling/protocols`, {
      params: filter?.exerciseId ? { exerciseId: filter.exerciseId } : undefined,
    }),

  // Lista esercizi recommendedForTesting (⭐) — endpoint dedicato (non admin-raw, funziona in produzione)
  // Response: array diretto (non paginato)
  listRecommendedExercises: (category?: string) =>
    axios.get<Array<{ _id: string; name: { en: string; it: string; es?: string }; category?: string; profilingHubProtocolId?: string }>>(
      `${baseUrl}/profiling/exercises/recommended`,
      { params: category ? { category } : undefined },
    ),

  // M5b closed loop: genera Block Programmazione da profilo VBT
  generateBlockFromProfile: (profileId: string) =>
    axios.post<{ blockId: string; title: string; sets: any[] }>(
      `${baseUrl}/profiling/blocks/from-profile/${profileId}`,
    ),

  // M5b auto-PR: coach conferma Alert AUTO_PR_DETECTED
  confirmAutoPR: (alertId: string) =>
    axios.post<{ confirmed: boolean; testResultId: string }>(
      `${baseUrl}/profiling/auto-pr/${alertId}/confirm`,
    ),

  // M5c: lista Alert AUTO_PR_DETECTED non confermati per atleta
  listAutoPRAlerts: (athleteId?: string) =>
    axios.get<{ docs: any[]; totalDocs: number }>(`${baseUrl}/admin-raw`, {
      params: {
        collection: 'alerts',
        filter: JSON.stringify({
          type: 'auto_pr_detected',
          read: false,
          ...(athleteId && { athleteId }),
        }),
        sort: JSON.stringify({ createdAt: -1 }),
        limit: 50,
      },
    }),

  // Scarta Alert (mark read senza creare testResult) via endpoint generico alerts
  discardAutoPR: (alertId: string) =>
    axios.patch<{ read: boolean }>(`${baseUrl}/alerts/${alertId}/read`),
};
